import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Fish, ChevronDown, Loader2, AlertCircle } from 'lucide-react'
import { format } from 'date-fns'
import { fishHealthApi, type FishHealthCheck } from '@/api/fishHealthApi'
import { HealthCheckReferenceCard } from './HealthCheckReferenceCard'

const riskColors = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

interface HealthCheckSelectorProps {
  farmId: string
  pondId: string
  pondName?: string
  selectedChecks: FishHealthCheck[]
  onChecksChange: (checks: FishHealthCheck[]) => void
  maxChecks?: number
  disabled?: boolean
}

export function HealthCheckSelector({
  farmId,
  pondId,
  pondName,
  selectedChecks,
  onChecksChange,
  maxChecks = 3,
  disabled = false,
}: HealthCheckSelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch health checks for the pond
  const { data: checksData, isLoading: isLoadingChecks } = useQuery({
    queryKey: ['fish-health', farmId, pondId],
    queryFn: () => fishHealthApi.listChecks(farmId, { pondId, limit: 50 }),
    enabled: !!farmId && !!pondId && isDropdownOpen,
  })

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const availableChecks = checksData?.data || []
  const selectedCheckIds = new Set(selectedChecks.map(c => c.id))
  const unselectedChecks = availableChecks.filter(c => !selectedCheckIds.has(c.id))
  const canAddMore = selectedChecks.length < maxChecks

  const handleSelectCheck = (check: FishHealthCheck) => {
    if (!canAddMore || selectedCheckIds.has(check.id)) return
    onChecksChange([...selectedChecks, check])
    setIsDropdownOpen(false)
  }

  const handleRemoveCheck = (checkId: string) => {
    onChecksChange(selectedChecks.filter(c => c.id !== checkId))
  }

  if (!pondId) {
    return (
      <div className="p-4 bg-slate-50 border border-slate-200/50 rounded-lg">
        <div className="flex items-center gap-2 text-slate-500">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Select a pond to reference health checks</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Reference Health Checks
          </label>
          <p className="text-xs text-slate-500 mt-0.5">
            Include historical check data and images for more accurate diagnosis ({selectedChecks.length}/{maxChecks})
          </p>
        </div>
      </div>

      {/* Selected Checks */}
      {selectedChecks.length > 0 && (
        <div className="space-y-2">
          {selectedChecks.map((check) => (
            <HealthCheckReferenceCard
              key={check.id}
              check={check}
              pondName={pondName}
              onRemove={() => handleRemoveCheck(check.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Add Check Button / Dropdown */}
      {canAddMore && (
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            className={`
              w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed
              transition-colors text-sm font-medium
              ${disabled
                ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:bg-emerald-50/50 hover:text-emerald-700 cursor-pointer'
              }
            `}
          >
            <Plus className="h-4 w-4" />
            Add Health Check Reference
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg">
              <div className="p-2 border-b border-slate-200/50">
                <p className="text-xs font-medium text-slate-500">
                  Select a health check from {pondName || 'this pond'}
                </p>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {isLoadingChecks ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
                  </div>
                ) : unselectedChecks.length > 0 ? (
                  <div className="py-1">
                    {unselectedChecks.map((check) => (
                      <button
                        key={check.id}
                        type="button"
                        onClick={() => handleSelectCheck(check)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="p-1.5 bg-blue-50 rounded border border-blue-200/50">
                          <Fish className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">
                              {format(new Date(check.checkedAt), 'MMM d, yyyy • h:mm a')}
                            </span>
                            {check.riskLevel && (
                              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${riskColors[check.riskLevel]}`}>
                                {check.riskLevel}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                            {check.healthScore !== undefined && (
                              <span>Score: {check.healthScore.toFixed(0)}</span>
                            )}
                            {check.imageIds && check.imageIds.length > 0 && (
                              <span>{check.imageIds.length} photos</span>
                            )}
                            {check.parasiteCode && (
                              <span className="text-amber-600">Parasite: {check.parasiteCode}</span>
                            )}
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-slate-400 rotate-[-90deg]" />
                      </button>
                    ))}
                  </div>
                ) : availableChecks.length > 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-500">All checks have been selected</p>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Fish className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No health checks found for this pond</p>
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-slate-200/50 bg-slate-50 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(false)}
                  className="w-full text-xs text-slate-500 hover:text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Max reached message */}
      {!canAddMore && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Maximum of {maxChecks} health checks reached
        </p>
      )}
    </div>
  )
}

export default HealthCheckSelector
